import { useState, useEffect, useRef, useCallback } from "react";
import { Button, Col, Form, FormInstance, Input, message, Row, Select, Switch, Upload } from "antd";
import regex from '../../utils/helper'
import { UploadOutlined } from '@ant-design/icons';
import { stepperMoving } from "../../stores/actions";
import { request } from '../../../../shared/src/apis';
import { useDispatch, useSelector } from "react-redux";
import { getOnePartnerApi, getPartnersTypesApi } from "../../utils/apisConsts";
import { arabicInputError, englishInputError } from "../../utils/commonStringsConsts";
import { Spinner, OutPutResult } from '@operations/shared';

const { Option } = Select;

export default function EditPartnerForm() {
    const formRef = useRef<FormInstance<any> | null>(null);
    const validation: any = regex.regexValidation;
    const selectedPartnerId = useSelector((state: any) => state?.actionsReducer?.selectedPartnerId);
    const dispatch = useDispatch()
    const [sending, setSending] = useState(false);
    const [imageFile, setImageFile] = useState<any>([]);
    const [logoFileList, setLogoFileList] = useState<any[]>([]);
    const [categoriesValid, setCategoriesValid] = useState(true);
    const [categoriesArValid, setCategoriesArValid] = useState(true);
    const [partnerData, setPartnerData] = useState<any>();
    const [partnersTypes, setPartnersTypes] = useState<any>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [errorMode, setErrorMode] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isDisabled, setIsDisabled] = useState<boolean>();

    const toggleHiddenStatus = useCallback((checked: boolean) => {
        setIsDisabled(checked);
    }, []);

    useEffect(() => {
        setIsDisabled(partnerData?.isDisabled);
    }, [partnerData]);

    const getPartnersTypes = useCallback(async () => {
        setIsLoadingTypes(true);
        try {
            const apiData: any = await dispatch(request({ url: getPartnersTypesApi, method: "get" }));
            const res = await apiData?.data;

            if (res?.status === 200) {
                setPartnersTypes(res?.partnersTypes);
                setIsLoadingTypes(false);
                return;
            }
            
            setErrorMode(true);
            setIsLoadingTypes(false);
            setErrorMessage(res?.message);
        } catch (error) {
            setErrorMode(true);
            setIsLoadingTypes(false);
            setErrorMessage('Something went wrong, please try again');
        }
    }, [dispatch]);

    const getPartnerById = useCallback(async () => {
        setIsLoading(true);
        
        try {
            const apiData: any = await dispatch(request({
                url: `${getOnePartnerApi}${selectedPartnerId}`,
                method: "get",
            }));
            
            const res = apiData?.data;
            
            if (res?.status === 200) {
                setPartnerData(res?.partnerDetails);
                setIsDisabled(res?.partnerDetails?.isDisabled)
                setIsLoading(false);
                return res?.partnerDetails;
            }
            
            setErrorMode(true);
            setIsLoading(false);
            setErrorMessage(res?.message || "Failed to load partner");
        } catch (error) {
            setErrorMode(true);
            setIsLoading(false);
            setErrorMessage("Something went wrong, please try again");
        }
    }, [selectedPartnerId, dispatch]);

    useEffect(() => {
        getPartnerById();
        getPartnersTypes();
    }, [getPartnerById, getPartnersTypes]);

    useEffect(() => {
        if (partnerData?.logoUrl) {
            setLogoFileList([
                {
                    uid: '-1',
                    name: 'logo.png',
                    status: 'done',
                    url: partnerData.logoUrl,
                }
            ]);

            formRef.current?.setFieldsValue({
                logoUrl: [
                    {
                        uid: '-1',
                        name: 'logo.png',
                        status: 'done',
                        url: partnerData.logoUrl,
                    }
                ]
            });
        }
    }, [partnerData]);

    const onFinishFailed = (e: any) => {
        console.log("🚀 ~ file: editPartner.tsx ~ line 24 ~ EditPartner ~ e", e);
    };

    const dataFile = {
        beforeUpload: (file: { size: any; name: string }) => {
            let file_size = file.size / (1024 * 1024), maxSize = 2, size;

            if (file.size < 1024) size = file.size + ' B';
            else if (file.size < 1024 * 1024) size = Math.floor(file.size / 1024) + ' KB';
            else size = Math.floor(file.size / (1024 * 1024)) + ' MB';

            let acceptable_exe = /(\.png|\.jpg|\.jpeg)$/i;
            if (!acceptable_exe.exec(file.name)) {
                message.error(`The extension of (${file.name}) is not supported!`);
                return Upload.LIST_IGNORE;
            } else if (file_size > maxSize) {
                message.error(`File size = ${size} exceeds ${maxSize} MB`);
                return Upload.LIST_IGNORE;
            } else {
                return true;
            }
        }
    };

    const normFile = (e: any) => {
        setImageFile(e.file.originFileObj);
        return e.fileList || e.file;
    };

    const onCategoriesChange = (values: [], lang: string) => {
        let count = 0;
        const regex = lang === "en" ? validation.englishRegex : validation.arabicRegex;
        const setStateFn = lang === "en" ? setCategoriesValid : setCategoriesArValid;

        for (let index = 0; index < values.length; index++) {
            if (!regex.exec(values[index])) {
                count++;
                break;
            }
        }
        setStateFn(count === 0);
    };

    const submitEditPartner = async (values: any) => {
        setSending(true);
        values.logoUrl = imageFile instanceof File ? imageFile 
            : logoFileList[0]?.originFileObj || logoFileList[0]?.url;
        const categories = values?.categories?.join("|||")
        const categories_ar = values?.categories_ar?.join("|||")
        values.categories = categories
        values.categories_ar = categories_ar
        values.isDisabled = isDisabled ? true : false

        const formData = new FormData()
        formData.append("logoUrl", values?.logoUrl)
        formData.append("hotLine", values?.hotLine)
        for (let key in values) {
            (key !== "logoUrl" && key !== "hotLine") && formData.append(key, values[key])
        }
        try {
            const res: any = await dispatch(request({ url: `${getOnePartnerApi}${selectedPartnerId}`, method: "PATCH", data: formData }));
            setSending(false);
            if (res?.data?.status === 200 || res?.data?.status === 201) {
                message.success(res?.data?.message, 4);
                formRef.current!.resetFields();
                dispatch(stepperMoving(0));
            } else {
                message.error(res?.data?.status === 500 ? errorMessage : res?.data?.message, 4);
            }
        }
        catch (e: any) {
            setSending(false);
            message.error(e?.response?.data?.status === 500 ? errorMessage : e?.response?.data?.message, 4);
        }
    };

    return (
        <>
            {isLoading ? (
                <Spinner size={'large'} />
            ) : errorMode ? (
                <OutPutResult status={500} title={`Oop's`} subTitle={errorMessage} />
            ) : (
                <div className="createPartner">
                    <Form
                        name="basic"
                        ref={formRef}
                        initialValues={{ remember: true }}
                        onFinish={submitEditPartner}
                        onFinishFailed={onFinishFailed}
                        autoComplete="off"
                    >
                        <Row>
                            <Col span={12}>
                                <label>Name <span>*</span></label>
                                <Form.Item name="name" initialValue={partnerData?.name} rules={[
                                    { required: true, message: 'Required' },
                                    { pattern: validation.englishRegex, message: englishInputError }
                                ]}>
                                    <Input placeholder='Please enter partner name' disabled/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <label>Arabic Name <span>*</span></label>
                                <Form.Item name="name_ar" initialValue={partnerData?.name_ar} rules={[
                                    { required: true, message: 'Required' },
                                    { pattern: validation.arabicRegex, message: arabicInputError }
                                ]}>
                                    <Input placeholder='Please enter partner arabic name' />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <label htmlFor="">English Name for App <span>*</span></label>
                                <Form.Item name="displayName" initialValue={partnerData?.displayName} rules={[{ required: true, message: 'Required' }, { pattern: validation.englishRegex, message: englishInputError }]}>
                                    <Input placeholder='Please enter partner english name for app' />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <label>Description <span>*</span></label>
                                <Form.Item name="description" initialValue={partnerData?.description} rules={[
                                    { required: true, message: "Required" },
                                    { pattern: validation.englishRegexAllowNewlines, message: englishInputError }
                                ]}>
                                    <Input.TextArea placeholder='Please enter partner description' />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <label>Arabic Description <span>*</span></label>
                                <Form.Item name="description_ar" initialValue={partnerData?.description_ar} rules={[
                                    { required: true, message: "Required" },
                                    { pattern: validation.arabicRegex, message: arabicInputError }
                                ]}>
                                    <Input.TextArea placeholder='Please enter partner arabic description' />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <label>Categories <span>*</span></label>
                                <Form.Item name="categories" initialValue={partnerData?.categories?.split("|||")} rules={[{ required: true, message: "Required" }]}>
                                    <Select
                                        mode="tags"
                                        dropdownStyle={{ display: 'none' }}
                                        placeholder='Please enter categories in English'
                                        tokenSeparators={[',']}
                                        onChange={(values) => onCategoriesChange(values, "en")}
                                    />
                                </Form.Item>
                                {!categoriesValid && <span className="categoriesError">{englishInputError}</span>}
                            </Col>
                            <Col span={24}>
                                <label>Categories in Arabic <span>*</span></label>
                                <Form.Item name="categories_ar" initialValue={partnerData?.categories_ar?.split("|||")} rules={[{ required: true, message: "Required" }]}>
                                    <Select
                                        mode="tags"
                                        dropdownStyle={{ display: 'none' }}
                                        placeholder='Please enter categories in Arabic'
                                        tokenSeparators={[',']}
                                        onChange={(values) => onCategoriesChange(values, "ar")}
                                    />
                                </Form.Item>
                                {!categoriesArValid && <span className="categoriesError">{arabicInputError}</span>}
                            </Col>
                            <Col span={12}>
                                <label>Hotline <span>*</span></label>
                                <Form.Item name="hotLine" initialValue={partnerData?.hotLine} rules={[
                                    { required: true, message: "Required" },
                                    { pattern: validation.hotlineRegex, message: "Please enter a valid hotline" }
                                ]}>
                                    <Input placeholder='Please enter partner hotline' />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <label>Partners Types <span>*</span></label>
                                <Form.Item name="partnerTypes" initialValue={partnerData?.partnerTypes} rules={[{ required: true, message: "Required" }]}>
                                    <Select placeholder='Please select partners type' mode="multiple" loading={isLoadingTypes}>
                                        {partnersTypes?.map((type: any, key: number) => (
                                            <Option value={type?.name} name={type} key={key}>
                                                {type?.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <label>Work Time <span>*</span></label>
                                <Form.Item name="workTime" initialValue={partnerData?.workTime} rules={[
                                    { required: true, message: "Required" },
                                    { pattern: validation.englishRegexAllowNewlines, message: englishInputError }
                                ]}>
                                    <Input.TextArea placeholder='Please enter work time' />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <label>Work Time (In Arabic) <span>*</span></label>
                                <Form.Item name="workTime_ar" initialValue={partnerData?.workTime_ar} rules={[
                                    { required: true, message: "Required" },
                                    { pattern: validation.arabicRegex, message: arabicInputError }
                                ]}>
                                    <Input.TextArea placeholder='Please enter work time in Arabic' />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row>
                            <Col span={12}>
                                <label>Upload Logo <span>*</span></label>
                                <Form.Item
                                    name="logoUrl"
                                    valuePropName="fileList"
                                    getValueFromEvent={normFile}
                                    rules={[{ required: partnerData?.logoUrl ? false : true, message: 'Required' }]}>
                                    <Upload {...dataFile} maxCount={1} name="logo" action="/upload.do" listType="picture" defaultFileList={logoFileList}>
                                        <Button icon={<UploadOutlined />}>Click to upload the logo</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <label>Hidden</label>
                                <Form.Item name="isDisabled" valuePropName="checked" initialValue={partnerData?.isDisabled}>
                                    <Switch onChange={toggleHiddenStatus} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Col span={24}>
                            <Form.Item wrapperCol={{ offset: 0, span: 0 }}>
                                <Button type="primary" htmlType="submit" loading={sending}>
                                    Save
                                </Button>
                            </Form.Item>
                        </Col>
                    </Form>
                </div>
            )}
        </>
    );
};
